import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Menu, 
  Moon, 
  Sun, 
  Bell, 
  Search,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Info,
  X
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import SyncStatusBadge from './SyncStatusBadge';
import UploadStatusIndicator from './UploadStatusIndicator';

import { API_BASE_URL as API } from '../utils/apiConfig';

const viewTitles = {
  'executive': 'Executive Dashboard',
  'customers': 'Customer Analytics',
  'stores': 'Store Performance',
  'reports': 'AI Reports',
  'ai-chat': 'AI Conversation',
  'customer-tools': 'Points & Coupons'
};

const viewDescriptions = {
  'executive': 'Real-time overview of your loyalty program performance',
  'customers': 'Deep dive into customer segments, RFM, and cohorts',
  'stores': 'Compare store performance and identify opportunities',
  'reports': 'Generate and download comprehensive analytics reports',
  'ai-chat': 'Chat with your data using natural language',
  'customer-tools': 'Manually credit points or assign coupons to a customer'
};

export default function DashboardHeader({ activeView, onMenuClick }) {
  const { theme, toggleTheme } = useTheme();
  const { selectedBrand } = useChat();
  const { isSuperAdmin } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);

  // Fetch sync alerts for super admins
  const fetchAlerts = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await fetch(`${API}/api/settings/sync-alerts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const dismissAlert = async (brandId, alertType) => {
    try {
      await fetch(`${API}/api/settings/sync-alerts/dismiss?brand_id=${brandId}&alert_type=${alertType}`, {
        method: 'POST',
        credentials: 'include'
      });
      setAlerts(prev => prev.filter(a => !(a.brand_id === brandId && a.type === alertType)));
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const totalAlertCount = criticalCount + warningCount;

  return (
    <header className="h-16 px-6 flex items-center justify-between border-b border-border bg-card/50 backdrop-blur-sm" data-testid="dashboard-header">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onMenuClick}
          className="lg:hidden"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-lg font-semibold">{viewTitles[activeView]}</h1>
          <p className="text-sm text-muted-foreground hidden sm:block">
            {viewDescriptions[activeView]}
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Upload Status Indicator */}
        <UploadStatusIndicator />

        {/* Sync Status Badge */}
        <SyncStatusBadge className="hidden sm:flex" />

        {/* Search */}
        <Button variant="ghost" size="icon" className="hidden sm:flex">
          <Search className="w-5 h-5" />
        </Button>

        {/* Refresh */}
        <Button variant="ghost" size="icon" title="Refresh data">
          <RefreshCw className="w-5 h-5" />
        </Button>

        {/* Export */}
        <Button variant="ghost" size="icon" title="Export" className="hidden sm:flex">
          <Download className="w-5 h-5" />
        </Button>

        {/* Notifications - Sync Alerts for Super Admin */}
        {isSuperAdmin ? (
          <Popover open={alertsOpen} onOpenChange={setAlertsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                data-testid="sync-alerts-btn"
              >
                <Bell className="w-5 h-5" />
                {totalAlertCount > 0 && (
                  <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    criticalCount > 0 ? 'bg-red-500' : 'bg-yellow-500'
                  }`}>
                    {totalAlertCount > 9 ? '9+' : totalAlertCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-3 border-b border-border">
                <h4 className="font-semibold">Sync Alerts</h4>
                <p className="text-xs text-muted-foreground">
                  {totalAlertCount === 0 ? 'No active alerts' : `${totalAlertCount} alert${totalAlertCount !== 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {alerts.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm">All systems healthy</p>
                  </div>
                ) : (
                  alerts.slice(0, 10).map((alert, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 border-b border-border last:border-0 ${
                        alert.severity === 'critical' ? 'bg-red-500/10' :
                        alert.severity === 'warning' ? 'bg-yellow-500/10' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {alert.severity === 'critical' && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
                        {alert.severity === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />}
                        {alert.severity === 'info' && <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{alert.message}</p>
                          <p className="text-xs text-muted-foreground truncate">{alert.details}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 flex-shrink-0"
                          onClick={() => dismissAlert(alert.brand_id, alert.type)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {alerts.length > 0 && (
                <div className="p-2 border-t border-border">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-xs"
                    onClick={() => { setAlertsOpen(false); window.location.href = '/dashboard/data-sync-history'; }}
                  >
                    View Data Sync Dashboard
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        ) : (
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="w-5 h-5" />
          </Button>
        )}

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>

        {/* Brand indicator */}
        {selectedBrand && (
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">{selectedBrand.name}</span>
          </div>
        )}
      </div>
    </header>
  );
}
